import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3, PieChart as PieChartIcon, LineChart as LineChartIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

type ChartType = 'bar' | 'pie' | 'line';

interface ChartSelectorProps {
  selected: ChartType;
  onChange: (type: ChartType) => void;
}

function ChartTypeSelector({ selected, onChange }: ChartSelectorProps) {
  return (
    <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange('bar')}
        className={cn(
          "h-7 px-2",
          selected === 'bar' && "bg-background shadow-sm"
        )}
      >
        <BarChart3 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange('pie')}
        className={cn(
          "h-7 px-2",
          selected === 'pie' && "bg-background shadow-sm"
        )}
      >
        <PieChartIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onChange('line')}
        className={cn(
          "h-7 px-2",
          selected === 'line' && "bg-background shadow-sm"
        )}
      >
        <LineChartIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface DataItem {
  name: string;
  value: number;
  fill?: string;
}

interface DashboardChartProps {
  title: string;
  data: DataItem[];
  config: ChartConfig;
}

const COLORS = [
  'hsl(217, 91%, 60%)',
  'hsl(142, 71%, 45%)',
  'hsl(43, 96%, 56%)',
  'hsl(0, 84%, 60%)',
  'hsl(262, 83%, 58%)',
  'hsl(199, 89%, 48%)',
];

export function DashboardChart({ title, data, config }: DashboardChartProps) {
  const [chartType, setChartType] = useState<ChartType>('bar');

  const dataWithColors = data.map((item, index) => ({
    ...item,
    fill: item.fill || COLORS[index % COLORS.length],
  }));

  return (
    <Card className="card-interactive min-w-0">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-base font-medium">{title}</CardTitle>
        <ChartTypeSelector selected={chartType} onChange={setChartType} />
      </CardHeader>
      <CardContent className="min-w-0">
        <ChartContainer config={config} className="h-[250px] w-full min-w-0">
          {chartType === 'bar' ? (
            <BarChart data={dataWithColors} layout="vertical" margin={{ left: 0, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
              <XAxis type="number" />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={100} 
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {dataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          ) : chartType === 'pie' ? (
            <PieChart>
              <Pie
                data={dataWithColors}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                innerRadius={40}
                paddingAngle={2}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {dataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
            </PieChart>
          ) : (
            <LineChart data={dataWithColors} margin={{ left: 0, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tickLine={false} axisLine={false} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="hsl(217, 91%, 60%)" 
                strokeWidth={2}
                dot={{ fill: 'hsl(217, 91%, 60%)', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

interface OKRsByStatusChartProps {
  okrsPorStatus: Record<string, number>;
}

export function OKRsByStatusChart({ okrsPorStatus }: OKRsByStatusChartProps) {
  const data: DataItem[] = [
    { name: 'A Fazer', value: okrsPorStatus.a_fazer || 0, fill: 'hsl(43, 96%, 56%)' },
    { name: 'Fazendo', value: okrsPorStatus.fazendo || 0, fill: 'hsl(217, 91%, 60%)' },
    { name: 'Pausado', value: okrsPorStatus.pausado || 0, fill: 'hsl(215, 16%, 57%)' },
    { name: 'Feito', value: okrsPorStatus.feito || 0, fill: 'hsl(142, 71%, 45%)' },
    { name: 'Cancelado', value: okrsPorStatus.cancelado || 0, fill: 'hsl(0, 84%, 60%)' },
  ].filter(item => item.value > 0);

  const config: ChartConfig = {
    a_fazer: { label: 'A Fazer', color: 'hsl(43, 96%, 56%)' },
    fazendo: { label: 'Fazendo', color: 'hsl(217, 91%, 60%)' },
    pausado: { label: 'Pausado', color: 'hsl(215, 16%, 57%)' },
    feito: { label: 'Feito', color: 'hsl(142, 71%, 45%)' },
    cancelado: { label: 'Cancelado', color: 'hsl(0, 84%, 60%)' },
  };

  return <DashboardChart title="OKRs por Status" data={data} config={config} />;
}

interface DepartmentData {
  id: string;
  nome: string;
  okrCount: number;
}

interface OKRsByDepartmentChartProps {
  departments: DepartmentData[];
}

export function OKRsByDepartmentChart({ departments }: OKRsByDepartmentChartProps) {
  const data: DataItem[] = departments.map((dep, index) => ({
    name: dep.nome,
    value: dep.okrCount,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = departments.reduce((acc, dep) => {
    acc[dep.id] = { label: dep.nome };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="OKRs por Departamento" data={data} config={config} />;
}

interface UserData {
  tipo: string;
  count: number;
}

interface UsersByTypeChartProps {
  users: UserData[];
}

export function UsersByTypeChart({ users }: UsersByTypeChartProps) {
  const typeLabels: Record<string, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrador',
    comum: 'Usuário',
  };

  const data: DataItem[] = users.map((user, index) => ({
    name: typeLabels[user.tipo] || user.tipo,
    value: user.count,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = users.reduce((acc, user) => {
    acc[user.tipo] = { label: typeLabels[user.tipo] || user.tipo };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Usuários por Tipo" data={data} config={config} />;
}

interface ProgressData {
  name: string;
  value: number;
}

interface ProgressByDepartmentChartProps {
  data: ProgressData[];
}

export function ProgressByDepartmentChart({ data }: ProgressByDepartmentChartProps) {
  const dataWithColors = data.map((item, index) => ({
    ...item,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.name] = { label: item.name };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Progresso Médio por Departamento" data={dataWithColors} config={config} />;
}

// Users by Department Chart
interface UsersByDepartmentData {
  departamento: string;
  count: number;
}

interface UsersByDepartmentChartProps {
  data: UsersByDepartmentData[];
}

export function UsersByDepartmentChart({ data }: UsersByDepartmentChartProps) {
  const chartData: DataItem[] = data.map((item, index) => ({
    name: item.departamento,
    value: item.count,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.departamento] = { label: item.departamento };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Usuários por Departamento" data={chartData} config={config} />;
}

// OKRs by Company Chart (for superadmin)
interface OKRsByCompanyData {
  empresa: string;
  count: number;
}

interface OKRsByCompanyChartProps {
  data: OKRsByCompanyData[];
}

export function OKRsByCompanyChart({ data }: OKRsByCompanyChartProps) {
  const chartData: DataItem[] = data.map((item, index) => ({
    name: item.empresa,
    value: item.count,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.empresa] = { label: item.empresa };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="OKRs por Empresa" data={chartData} config={config} />;
}

// Users by Company Chart (for superadmin)
interface UsersByCompanyData {
  empresa: string;
  count: number;
}

interface UsersByCompanyChartProps {
  data: UsersByCompanyData[];
}

export function UsersByCompanyChart({ data }: UsersByCompanyChartProps) {
  const chartData: DataItem[] = data.map((item, index) => ({
    name: item.empresa,
    value: item.count,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.empresa] = { label: item.empresa };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Usuários por Empresa" data={chartData} config={config} />;
}

// Completion Rate by Department Chart
interface CompletionRateData {
  name: string;
  value: number;
}

interface CompletionRateChartProps {
  data: CompletionRateData[];
}

export function CompletionRateByDepartmentChart({ data }: CompletionRateChartProps) {
  const chartData: DataItem[] = data.map((item, index) => ({
    name: item.name,
    value: item.value,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.name] = { label: item.name };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Taxa de Conclusão por Departamento (%)" data={chartData} config={config} />;
}

// Key Results Progress Chart
interface KRProgressData {
  name: string;
  value: number;
}

interface KRProgressChartProps {
  data: KRProgressData[];
}

export function KRProgressChart({ data }: KRProgressChartProps) {
  const chartData: DataItem[] = data.map((item, index) => ({
    name: item.name,
    value: item.value,
    fill: COLORS[index % COLORS.length],
  }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.name] = { label: item.name };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Progresso dos Key Results" data={chartData} config={config} />;
}

// Company Progress Ranking Chart (for superadmin)
interface CompanyMetricData {
  empresa: string;
  value: number;
}

interface CompanyChartProps {
  data: CompanyMetricData[];
}

export function CompanyProgressRankingChart({ data }: CompanyChartProps) {
  const chartData: DataItem[] = [...data]
    .sort((a, b) => b.value - a.value)
    .map((item, index) => ({
      name: item.empresa,
      value: item.value,
      fill: COLORS[index % COLORS.length],
    }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.empresa] = { label: item.empresa };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Ranking de Progresso Médio por Empresa (%)" data={chartData} config={config} />;
}

export function CompletionRateByCompanyChart({ data }: CompanyChartProps) {
  const chartData: DataItem[] = [...data]
    .sort((a, b) => b.value - a.value)
    .map((item, index) => ({
      name: item.empresa,
      value: item.value,
      fill: COLORS[index % COLORS.length],
    }));

  const config: ChartConfig = data.reduce((acc, item) => {
    acc[item.empresa] = { label: item.empresa };
    return acc;
  }, {} as ChartConfig);

  return <DashboardChart title="Taxa de Conclusão por Empresa (%)" data={chartData} config={config} />;
}
